import { Injectable } from "@angular/core";
import {
  Intercom,
  hide as intercomHide,
  onHide,
  onShow,
  onUnreadCountChange,
  shutdown as intercomShutdown,
  show as intercomShow,
  showNewMessage as intercomShowNewMessage,
  trackEvent as intercomTrackEvent,
  update as intercomUpdate,
} from "@intercom/messenger-js-sdk";
import { BehaviorSubject, Observable } from "rxjs";

import { IntercomBootSettings } from "./intercom.types";

@Injectable({ providedIn: "root" })
export class IntercomService {
  public readonly unreadCount$: Observable<number>;
  public readonly isShown$: Observable<boolean>;

  private readonly $unreadCount = new BehaviorSubject<number>(0);
  private readonly $isShown = new BehaviorSubject<boolean>(false);

  private booted = false;
  private currentUserId: string | null = null;
  private currentCompanyId: string | null = null;
  private pendingEvents: { name: string; metadata?: Record<string, unknown> }[] = [];

  constructor() {
    this.unreadCount$ = this.$unreadCount.asObservable();
    this.isShown$ = this.$isShown.asObservable();
  }

  public boot(settings: IntercomBootSettings): void {
    const nextUserId = settings.user_id ?? null;
    const nextCompanyId = settings.company?.company_id ?? null;

    if (this.booted) {
      const identityChanged = nextUserId !== this.currentUserId || nextCompanyId !== this.currentCompanyId;
      if (!identityChanged) {
        intercomUpdate(settings);
        return;
      }
      intercomShutdown();
      this.booted = false;
      this.$unreadCount.next(0);
      this.$isShown.next(false);
    }

    Intercom(settings);
    onUnreadCountChange((count: number) => {
      this.$unreadCount.next(count ?? 0);
    });
    onShow(() => this.$isShown.next(true));
    onHide(() => this.$isShown.next(false));
    this.booted = true;
    this.currentUserId = nextUserId;
    this.currentCompanyId = nextCompanyId;

    if (this.pendingEvents.length > 0) {
      const queued = this.pendingEvents;
      this.pendingEvents = [];
      for (const event of queued) {
        intercomTrackEvent(event.name, event.metadata);
      }
    }
  }

  public update(settings: Partial<IntercomBootSettings>): void {
    if (!this.booted) return;
    intercomUpdate(settings);
  }

  public show(): void {
    if (!this.booted) return;
    intercomShow();
  }

  public hide(): void {
    if (!this.booted) return;
    intercomHide();
  }

  public toggle(): void {
    if (!this.booted) return;
    if (this.$isShown.getValue()) {
      intercomHide();
    } else {
      intercomShow();
    }
  }

  public showNewMessage(prePopulatedContent: string): void {
    if (!this.booted) return;
    intercomShowNewMessage(prePopulatedContent);
  }

  public shutdown(): void {
    if (!this.booted) return;
    intercomShutdown();
    this.booted = false;
    this.currentUserId = null;
    this.currentCompanyId = null;
    this.pendingEvents = [];
    this.$unreadCount.next(0);
    this.$isShown.next(false);
  }

  public trackEvent(name: string, metadata?: Record<string, unknown>): void {
    if (!this.booted) {
      this.pendingEvents.push({ name, metadata });
      return;
    }
    intercomTrackEvent(name, metadata);
  }
}
